import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/joy";
import NavBar from "./NavBar";
import DarkTypography from "./DarkTypography";
import CompanyBrandBackdrop from "./CompanyBrandBackdrop";
import type { BrandBackdropModuleId } from "../../styles/globalStyles";
import { colorTokens } from "../../styles/globalStyles";

export const NO_ACCESS_MESSAGE =
  "Aucun droit sur ce menu, veuillez contacter votre administrateur.";

/** Shown when the user may open the module but no sidebar page matches their permissions. */
export const MODULE_EMPTY_SIDEBAR_MESSAGE =
  "Vous avez accès à ce module, mais aucune page n'est disponible avec vos droits actuels. Contactez votre administrateur.";

type DashboardPageProps = {
  sidebarConfig: {
    label: string;
    icon: JSX.Element;
    component: JSX.Element;
  }[];
  brandBackdropModule: BrandBackdropModuleId;
};

export default function DashboardPage({ sidebarConfig, brandBackdropModule }: DashboardPageProps) {
  const config = React.useMemo(() => sidebarConfig ?? [], [sidebarConfig]);
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = React.useState<JSX.Element | null>(config[0]?.component ?? null);
  const [title, setTitle] = React.useState(config[0]?.label || "");

  React.useEffect(() => {
    if (config.length === 0) {
      setPage(null);
      setTitle("");
      return;
    }
    setPage((prev) => (prev == null ? config[0].component : prev));
    setTitle((prev) => (prev === "" ? config[0].label : prev));
  }, [config]);

  React.useEffect(() => {
    const desired = (location.state as { select?: string } | undefined)?.select;
    if (!desired) return;
    const match = config.find((it) => it.label === desired);
    if (match) {
      setPage(match.component);
      setTitle(match.label);
    }
  }, [location.state, config]);

  const hasNoAccess = config.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        overflow: "hidden",
        backgroundColor: colorTokens.bg.app,
      }}
    >
      <NavBar
        config={config}
        setPage={(p, t) => {
          setPage(p);
          setTitle(t);
        }}
        pageTitle={hasNoAccess ? "" : title}
      />
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
          backgroundColor: colorTokens.bg.app,
          ...(hasNoAccess
            ? { display: "flex", alignItems: "center", justifyContent: "center", p: 3 }
            : { display: "flex", flexDirection: "column" }),
        }}
      >
        <CompanyBrandBackdrop module={brandBackdropModule} />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "transparent",
            ...(hasNoAccess ? { alignItems: "center", justifyContent: "center" } : {}),
          }}
        >
          {hasNoAccess ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                maxWidth: { xs: "95vw", sm: 440 },
                px: 1,
              }}
            >
              <DarkTypography level="body-lg" colorVariant="muted" sx={{ textAlign: "center" }}>
                {MODULE_EMPTY_SIDEBAR_MESSAGE}
              </DarkTypography>
              <Button
                variant="soft"
                color="primary"
                onClick={() => navigate("/")}
                sx={{ minHeight: 44 }}
              >
                Retour au menu
              </Button>
            </Box>
          ) : (
            (page ?? config[0]?.component)
          )}
        </Box>
      </Box>
    </Box>
  );
}
